import React, { useMemo, useRef, ReactNode } from "react";
import { DataTableContext } from "../context";
import {
  DataTableProps,
  DataTableContextValue,
  DataTableRow,
  DataTableColumn,
} from "../types";
import { PanelColor } from "../../../props";
import { cn, panelBackgroundStyles, panelBorderStyles } from "../../../styles";
import { Table } from "./Table";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { Toolbar } from "./Toolbar";
import { Row } from "./Row";
import { Cell } from "./Cell";
import { Group } from "./Group";
import { SortingState } from "../hooks/useSorting";
import { FilteringState } from "../hooks/useFiltering";
import { PaginationState } from "../hooks/usePagination";
import { SelectionState } from "../hooks/useSelection";
import { ExpansionState } from "../hooks/useExpansion";
import { ColumnLayoutState } from "../hooks/useColumnLayout";
import { PresetsState } from "../hooks/useColumnPresets";
import { GroupingState } from "../hooks/useGrouping";
import { useVirtualScroll } from "../hooks/useVirtualScroll";
import { useFrozenColumns } from "../hooks/useFrozenColumns";
import { LoadingIndicator } from "../../../atoms";
import { Typography } from "../../../atoms/Typography";
import { ScrollArea } from "../../../layouts/ScrollArea";

export interface DataTableFullProps<TRow> extends DataTableProps<TRow> {
  sorting?: SortingState;
  filtering?: FilteringState;
  pagination?: PaginationState;
  selection?: SelectionState<TRow>;
  expansion?: ExpansionState;
  columnLayout?: ColumnLayoutState<TRow>;
  presets?: PresetsState;
  grouping?: GroupingState<TRow>;
  /** Renders cell *content* only; the Cell itself (freezing, alignment, width) stays owned by the Row. */
  renderCellContent?: (
    row: DataTableRow<TRow>,
    column: DataTableColumn<TRow>,
  ) => ReactNode;
  children?: ReactNode;
}

// Needed so virtualizer spacer rows span all columns
function SpacerRow({ height }: { height: number }) {
  return (
    <tr aria-hidden>
      <td style={{ height, padding: 0, border: 0 }} />
    </tr>
  );
}

function DataTableRoot<TRow>({
  table,
  sorting,
  filtering,
  pagination,
  selection,
  expansion,
  columnLayout,
  presets,
  grouping,
  loading = false,
  noRows,
  renderRowExpansion,
  alwaysExpanded = false,
  rowClasses,
  rowBackgroundClass,
  cellClasses,
  minHeight,
  fullHeight = false,
  naturalHeight = false,
  enumerations = {},
  localizations = {},
  dense = false,
  flush = false,
  color = PanelColor.level1,
  showToolbar = false,
  defaultFilterExpanded = false,
  filterOnlyToolbar = false,
  renderFooterActions,
  renderBatchActions,
  renderToolbarActions,
  onDownload,
  virtualScroll = false,
  onLoadMore,
  isFetchingMore = false,
  estimatedRowHeight,
  renderCellContent,
  className,
  children,
  ...props
}: DataTableFullProps<TRow>) {
  const visibleColumns = useMemo(
    () => table.columns.filter((col) => !col.hidden),
    [table.columns],
  );
  const hasExpansionColumn = !!(expansion && renderRowExpansion && !alwaysExpanded);
  const frozen = useFrozenColumns(visibleColumns, !!selection, hasExpansionColumn);

  const contextValue = useMemo<DataTableContextValue<TRow>>(
    () => ({
      table,
      dense,
      color,
      enumerations,
      localizations,
      renderRowExpansion,
      alwaysExpanded,
      rowClasses,
      rowBackgroundClass,
      cellClasses,
      renderBatchActions,
      renderToolbarActions,
      onDownload,
      sorting,
      filtering,
      pagination,
      selection,
      expansion,
      columnLayout,
      presets,
      grouping,
      loading,
      virtualScroll,
      isFetchingMore,
      frozen,
    }),
    [
      table,
      dense,
      color,
      enumerations,
      localizations,
      renderRowExpansion,
      alwaysExpanded,
      rowClasses,
      rowBackgroundClass,
      cellClasses,
      renderBatchActions,
      renderToolbarActions,
      onDownload,
      sorting,
      filtering,
      pagination,
      selection,
      expansion,
      columnLayout,
      presets,
      grouping,
      loading,
      virtualScroll,
      isFetchingMore,
      frozen,
    ],
  );

  const footerActions = renderFooterActions?.(table);

  const virtual = useVirtualScroll(
    virtualScroll
      ? {
          count: table.rows.length,
          estimatedRowHeight,
          onLoadMore,
          isFetchingMore,
        }
      : { count: 0 },
  );

  const hasRows = virtualScroll
    ? table.rows.length > 0
    : React.Children.count(children) > 0;

  return (
    <DataTableContext.Provider value={contextValue}>
      <div
        className={cn(
          "flex flex-col",
          !naturalHeight && "overflow-hidden",
          !flush && "rounded-xl border shadow-lg",
          panelBackgroundStyles[color],
          panelBorderStyles[color],
          fullHeight && "h-full",
          className,
        )}
        style={{ minHeight }}
        {...props}
      >
        {(showToolbar || filterOnlyToolbar) && (
          <Toolbar
            defaultFilterExpanded={defaultFilterExpanded}
            filterOnlyToolbar={filterOnlyToolbar}
          />
        )}

        <div
          className={cn(
            naturalHeight ? undefined : "relative grow",
            !naturalHeight && (fullHeight ? "h-full" : "min-h-[200px]"),
          )}
        >
          {naturalHeight ? (
            <ScrollArea direction="horizontal" tabIndex={-1}>
              <Table>
                <Header />
                <tbody>
                  {children}
                  {loading && (
                    <tr>
                      <td className="absolute inset-0 flex justify-center items-center">
                        <LoadingIndicator size="small" delay={false} />
                      </td>
                    </tr>
                  )}
                  {!loading && !hasRows && (
                    <tr>
                      <td className="absolute inset-0 flex justify-center items-center text-front-500">
                        {noRows ?? (
                          <Typography.Span local>NoData</Typography.Span>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </ScrollArea>
          ) : (
            <div className="absolute inset-0">
              <ScrollArea
                ref={virtualScroll ? virtual.scrollRef : undefined}
                direction="both"
                tabIndex={-1}
                className="h-full"
              >
                <Table>
                  <Header />
                  <tbody>
                    {virtualScroll ? (
                      <>
                        <SpacerRow height={virtual.paddingTop} />
                        {virtual.virtualItems.map((vi) => (
                          <Row
                            key={vi.key}
                            row={table.rows[vi.index]}
                            index={vi.index}
                            renderCellContent={renderCellContent}
                            measureElement={virtual.measureElement}
                          />
                        ))}
                        <SpacerRow height={virtual.paddingBottom} />
                        {isFetchingMore && (
                          <tr>
                            <td
                              colSpan={
                                table.columns.filter((c) => !c.hidden).length
                              }
                              className="py-3"
                            >
                              <div className="flex justify-center">
                                <LoadingIndicator size="small" delay={false} />
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    ) : (
                      children
                    )}
                    {loading && (
                      <tr>
                        <td className="absolute inset-0 flex justify-center items-center">
                          <LoadingIndicator size="small" delay={false} />
                        </td>
                      </tr>
                    )}
                    {!loading && !hasRows && (
                      <tr>
                        <td className="absolute inset-0 flex justify-center items-center text-front-500">
                          {noRows ?? (
                            <Typography.Span local>NoData</Typography.Span>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </ScrollArea>
            </div>
          )}
        </div>

        {!virtualScroll && <Footer actions={footerActions} />}
        {virtualScroll && footerActions && <Footer actions={footerActions} />}
      </div>
    </DataTableContext.Provider>
  );
}

// Compound component pattern
export const DataTable = Object.assign(DataTableRoot, {
  Row,
  Cell,
  Group,
});
